import type { TableColumn } from '../../components/shared';
import type { AssignmentDto } from '../../api/hr-assignment';
import { periodText, type EducationDto, type CareerDto } from '../../api/hr-records';
import type { AwardDto } from '../../api/hr-awards';
import type { UserEvaluationDto } from '../../api/hr-evaluations';
import type { CredentialDto, UserTrainingDto } from '../../api/hr-trainings';
// 알약·줄바꿈 규칙은 이 파일들에 산다 — **모듈과 함께 다니게** 여기서 들여온다.
// (StaffRecordSection 은 이것을 안 들여와서, 직원 관리 화면에서는 `hrcard__nowrap` 이
//  아무 규칙도 없는 이름이었다 — 파일 경계는 사용 범위가 아니다.)
import './MyRecordCardPage.css';
import './HrRecordTables.css';

/**
 * 한 사람의 **인사기록 표 한 벌** — 두 화면이 나눠 쓴다 (2026-09-02 · 채택 3단계 ③).
 *
 * ── 왜 생겼나 ────────────────────────────────────────────────────────────
 * 같은 기록을 **본인**(`MyRecordCardPage`)과 **담당자**(`StaffRecordSection`)가 각각 제 표로
 * 그리고 있었다. `StaffRecordSection` 은 「복사해 두면 한쪽만 고쳐지는 날이 온다」고 적어 두고
 * 태어났는데, **그 날이 이미 와 있었다.** 실측한 어긋남:
 *
 *   · 포상·징계 — 본인은 **말소 · 감경**을 보는데 **담당자는 못 봤다**. 담당자 화면에 그 칸이
 *     아예 없어서, 말소된 징계를 「살아 있는 징계」로 읽을 수 있었다. 가장 나쁜 어긋남이다.
 *   · 인사평가 — 칸 차례가 **반대**였다(회차·소속·점수·등급 ↔ 회차·등급·점수·소속).
 *   · 경력 — 본인만 «개월»을 봤다. 자격 — 본인만 «증빙 · 임박/지남», 담당자만 «발급처».
 *   · 같은 값을 한쪽은 「종류」, 다른 쪽은 「구분」 / 「교육」과 「과정」으로 불렀다.
 *
 * ── 그래서 무엇을 했나 ──────────────────────────────────────────────────
 * **칸 정의를 한 벌만 둔다.** 두 화면은 이 열을 받아 정본 `Table`(E1)에 넘길 뿐이라,
 * 이제 한쪽만 고쳐지는 일이 **구조적으로 안 생긴다.**
 * ★ 합칠 때는 **아무것도 빼지 않고 «많이 말하는 쪽»으로 모았다**(소스 회귀 금지) —
 *   담당자는 말소·감경·개월·증빙·임박을, 본인은 발급처를 새로 본다.
 */

/** 발령의 한쪽 — 「개발1팀 사원 재직」. 바뀐 것이 없으면 «—». */
function sideText(s: AssignmentDto['before'] | null): string {
  if (!s) return '—';
  // ★ 담당자 쪽은 `employmentStatus` 를 안 그렸다 — 「휴직 → 복직」 발령이 «변화 없음»으로 보였다.
  return [s.teamName, s.position, s.employmentStatus].filter(Boolean).join(' · ') || '—';
}

const dim = (v: string | null | undefined) => <span className="hrpage__dim">{v ?? '—'}</span>;

/** 시행된 발령만 온다 — 예정은 아직 일어나지 않은 일이다. */
export const ASSIGNMENT_COLUMNS: ReadonlyArray<TableColumn<AssignmentDto>> = [
  { key: 'effectiveDate', header: '시행일', width: '106px', render: (a) => <span className="hrcard__nowrap">{a.effectiveDate}</span> },
  { key: 'kindLabel', header: '종류', width: '108px' },
  { key: 'before', header: '변경 전', render: (a) => dim(sideText(a.before)) },
  { key: 'after', header: '변경 후', render: (a) => sideText(a.after) },
];

export const EDUCATION_COLUMNS: ReadonlyArray<TableColumn<EducationDto>> = [
  { key: 'levelLabel', header: '구분', width: '92px' },
  { key: 'schoolName', header: '학교' },
  { key: 'major', header: '전공', render: (e) => dim(e.major) },
  { key: 'period', header: '기간', width: '150px', render: (e) => <span className="hrpage__dim hrcard__nowrap">{periodText(e.startYm, e.endYm)}</span> },
  { key: 'stateLabel', header: '상태', width: '86px', render: (e) => dim(e.stateLabel) },
];

/** 입사 «전» 경력이다 — 「경력」이라고만 적으면 사내 이력으로 읽힌다. */
export const CAREER_COLUMNS: ReadonlyArray<TableColumn<CareerDto>> = [
  { key: 'companyName', header: '회사' },
  { key: 'position', header: '직위', width: '96px', render: (c) => dim(c.position) },
  { key: 'duty', header: '담당', render: (c) => dim(c.duty) },
  { key: 'period', header: '기간', width: '150px', render: (c) => <span className="hrpage__dim hrcard__nowrap">{periodText(c.startYm, c.endYm)}</span> },
  // ★ 담당자 화면에는 없던 칸 — 경력 인정 개월은 담당자가 더 자주 보는 값이다.
  { key: 'months', header: '개월', width: '68px', numeric: true },
];

/**
 * 포상 · 징계.
 * ★★★ **말소·감경을 담당자가 못 보고 있었다.** 말소된 징계는 「더는 불이익에 쓰지 않는」 것인데,
 *    그 사실이 안 보이면 담당자가 살아 있는 징계로 읽는다 — 이 판에서 고친 것 중 제일 무겁다.
 */
export const AWARD_COLUMNS: ReadonlyArray<TableColumn<AwardDto>> = [
  { key: 'awardDate', header: '일자', width: '106px', render: (r) => <span className="hrcard__nowrap">{r.awardDate}</span> },
  {
    key: 'kindLabel', header: '종류', width: '104px',
    render: (r) => (
      <span className={`hrpage__pill hrcard__kind is-${(r.category ?? 'none').toLowerCase()}`}>{r.kindLabel}</span>
    ),
  },
  {
    key: 'reason', header: '사유',
    render: (r) => (
      <>
        {r.reason}
        {r.relievedBy && <span className="hrpage__pill hrcard__off">감경됨</span>}
      </>
    ),
  },
  { key: 'basis', header: '근거', render: (r) => dim(r.basis) },
  {
    key: 'expiresOn', header: '말소', width: '132px',
    render: (r) => (
      <span className="hrcard__nowrap">
        {r.expiresOn ?? '—'}
        {r.expired && <span className="hrpage__pill hrcard__off">말소됨</span>}
      </span>
    ),
  },
];

/**
 * 인사평가.
 * ★ 차례는 **회차 · 소속 · 점수 · 등급** — 점수에서 등급이 나오므로 그 순서다.
 *   담당자 화면은 이것이 뒤집혀 있었다(회차·등급·점수·소속).
 * ★ 확정 전 회차는 등급·점수가 **null 로 온다** — 바뀔 수 있는 값이라 서버가 말하지 않는다.
 *   「—」가 아니라 «확정 전» 이라고 적는다.
 */
export const EVALUATION_COLUMNS: ReadonlyArray<TableColumn<UserEvaluationDto>> = [
  { key: 'roundName', header: '회차', render: (e) => <span className="hrcard__nowrap">{e.roundName}</span> },
  { key: 'teamName', header: '소속', render: (e) => dim(e.teamName) },
  { key: 'score', header: '점수', width: '80px', numeric: true, render: (e) => (e.confirmed ? (e.score ?? '—') : '—') },
  {
    key: 'grade', header: '등급', width: '96px',
    render: (e) => (e.confirmed
      ? (e.grade ?? '—')
      : <span className="hrpage__pill hrcard__off">확정 전</span>),
  },
];

type CourseRow = UserTrainingDto['courses'][number];

/**
 * 교육.
 * ★ 이름 칸은 **「과정」** 이다 — 표 제목이 「교육」인데 칸 이름도 「교육」이면 같은 말이 겹친다.
 * ★★ 이수 칸은 **두 화면의 좋은 쪽을 합쳤다**: 알약(본인 쪽·눈에 띈다) + `completedOn` 이
 *    없을 때의 «이수»(담당자 쪽). 본인 쪽 알약은 `completedOn` 이 null 이면 **빈 알약**이었다.
 */
export const COURSE_COLUMNS: ReadonlyArray<TableColumn<CourseRow>> = [
  { key: 'name', header: '과정' },
  { key: 'kindLabel', header: '종류', width: '104px', render: (c) => dim(c.kindLabel) },
  { key: 'dueOn', header: '기한', width: '116px', render: (c) => <span className="hrpage__dim hrcard__nowrap">{c.dueOn ?? '—'}</span> },
  {
    key: 'done', header: '이수', width: '116px',
    render: (c) => (
      <span className="hrcard__nowrap">
        {c.done
          ? <span className="hrpage__pill hrcard__done">{c.completedOn ?? '이수'}</span>
          : <span className="hrpage__pill hrcard__todo">미이수</span>}
      </span>
    ),
  },
];

/** 기한이 지났는데 남은 줄 — 왼쪽에 경고 줄무늬. 글자(«미이수»)를 거들 뿐이다. */
export const courseRowClass = (c: CourseRow) => (c.done ? undefined : 'is-overdue');

/**
 * 자격 · 어학 — 서버가 **한 목록으로** 준다(`kind` 로 갈린다).
 * @param showKind 목록을 종류로 걸러 낸 화면(본인 카드의 「자격」·「어학」 탭)에서는 끈다 —
 *                 모든 줄이 같은 값이라 칸 하나가 통째로 낭비된다.
 */
export function credentialColumns(showKind: boolean): ReadonlyArray<TableColumn<CredentialDto>> {
  return [
    ...(showKind ? [{ key: 'kindLabel', header: '종류', width: '80px', render: (c: CredentialDto) => dim(c.kindLabel) }] : []),
    { key: 'title', header: '이름' },
    // ★ 본인 화면에는 없던 칸 — 「TOEIC 을 어디서 받았나」는 본인도 궁금한 값이다.
    { key: 'issuer', header: '발급', width: '132px', render: (c) => dim(c.issuer) },
    { key: 'acquiredOn', header: '취득', width: '116px', render: (c) => <span className="hrpage__dim hrcard__nowrap">{c.acquiredOn}</span> },
    {
      // ★ 담당자 화면에는 «임박 · 지남»이 없었다 — 만료일만 보고 지났는지 스스로 세야 했다.
      key: 'expiresOn', header: '만료', width: '148px',
      render: (c) => (
        <span className="hrcard__nowrap">
          {c.expiresOn ?? '—'}
          {c.expiry === 'EXPIRED' && <span className="hrpage__pill hrcard__todo">지남</span>}
          {c.expiry === 'SOON' && <span className="hrpage__pill hrcard__soon">임박</span>}
        </span>
      ),
    },
    {
      // ★ 「있음 / 만료 / 없음」 — 증명서에 실을 수 있는지를 가르는 값이다.
      key: 'hasProof', header: '증빙', width: '76px',
      render: (c) => dim(c.hasProof ? (c.onCertificate ? '있음' : '만료') : '없음'),
    },
  ];
}

export const credentialRowClass = (c: CredentialDto) =>
  (c.expiry === 'EXPIRED' || c.expiry === 'SOON' ? 'is-overdue' : undefined);
